import { createSelector } from 'reselect';
import { errorsToTouch, getLocalizedString } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { ProductPrice } from '../models/product-price';
import { ProductPriceFormValues } from '../models/form-values/product-price-form-values';
import { ProductPriceRequest } from '../api/models/product-price-request';
import { TaxClass } from '../models/tax-class';
import { defaultCurrencySelector } from '../../../../../../common/selectors/currency-selectors';
import { localesSelector } from '../../../../../../common/selectors/locale-selectors';
import { productDetailSelector } from '../../overview/store/product-detail-overview-selectors';
import { unitsSelector } from '../../../../../../common/selectors/units-selectors';

const productDetailPricesState = (state: AppState) => state.product.detail.prices;

/**
 * The shape the mask edits in: the currency of each price carried down onto its
 * scale and special prices, and a default for everything the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159) — compared against raw
 * server values, both that carrying-down and every default would read as input.
 */
const withDefaults = (
    productDetailPrice: ProductPrice | null | undefined,
    context: {
        locales: ReturnType<typeof localesSelector>;
        defaultCurrency: string;
        productDetail: ReturnType<typeof productDetailSelector>;
        units: ReturnType<typeof unitsSelector>;
    }
): ProductPriceFormValues => {
    const { locales, defaultCurrency, productDetail, units } = context;

    const currencyPrices = productDetailPrice?.currencyPrices?.map(currencyPrice => {
        return {
            ...currencyPrice,
            specialPrices: currencyPrice.specialPrices?.map(specialPrice => {
                return {
                    ...specialPrice,
                    currency: currencyPrice.currency,
                };
            }),
            scalePrices: currencyPrice.scalePrices?.map(scalePrice => {
                return {
                    ...scalePrice,
                    currency: currencyPrice.currency,
                };
            }),
        };
    });

    const formValues: ProductPriceFormValues = {
        isBestPrice: productDetailPrice?.isBestPrice || false,
        taxClass: productDetailPrice?.taxClass ?? TaxClass.Standard,
        priceQuantity: productDetailPrice?.priceQuantity ?? 1,
        priceReferenceQuantity: productDetailPrice?.priceReferenceQuantity ?? 1,
        currencyPrices:
            productDetailPrice?.currencyPrices && productDetailPrice.currencyPrices.length > 0
                ? currencyPrices
                : [
                      {
                          currency: defaultCurrency,
                          mainPrice: null,
                          scalePrices: [],
                          specialPrices: [],
                      },
                  ],

        orderUnit: getLocalizedString(
            locales,
            units.find(unit => unit.id === productDetail?.orderUnitId)?.translations
        ),
        contentUnit: getLocalizedString(
            locales,
            units.find(unit => unit.id === productDetail?.contentUnitId)?.translations
        ),
        contentQuantity: productDetail?.contentQuantity,
        minimumOrderQuantity: productDetail?.minimumOrderQuantity,
    };

    return formValues;
};

export const productDetailPricesFormDataSelector = createSelector(
    [localesSelector, defaultCurrencySelector, productDetailSelector, unitsSelector, productDetailPricesState],
    (locales, defaultCurrency, productDetail, units, detailPricesState) => ({
        formValues: withDefaults(detailPricesState.productDetailPrice, {
            locales,
            defaultCurrency,
            productDetail,
            units,
        }),
        errors: detailPricesState.errors,
        touched: errorsToTouch(detailPricesState.errors),
        supplierSpecialOffers: detailPricesState.supplierSpecialOffers,
    })
);

/** What the tab compares against to decide whether anything is unsaved. */
export const productDetailPricesBaselineSelector = createSelector(
    [localesSelector, defaultCurrencySelector, productDetailSelector, unitsSelector, productDetailPricesState],
    (locales, defaultCurrency, productDetail, units, detailPricesState) =>
        detailPricesState.baseline
            ? withDefaults(detailPricesState.baseline, { locales, defaultCurrency, productDetail, units })
            : undefined
);

export const productDetailPricesRequestSelector = createSelector(productDetailPricesFormDataSelector, formData => {
    const currencyPrices = formData.formValues.currencyPrices?.map(currencyPrice => {
        return {
            ...currencyPrice,
            specialPrices: currencyPrice.specialPrices
                ?.filter(specialPrice => specialPrice.price && specialPrice.startDate && specialPrice.endDate)
                // An empty step is the rule that has always applied: from the
                // first unit. Sent as an empty string it would be a fault.
                .map(specialPrice => ({
                    ...specialPrice,
                    minimumQuantity:
                        Number(specialPrice.minimumQuantity) > 0 ? Number(specialPrice.minimumQuantity) : 1,
                })),
            scalePrices: currencyPrice.scalePrices?.filter(scalePrice => scalePrice.price && scalePrice.quantity),
        };
    });
    const request: ProductPriceRequest = {
        isBestPrice: undefined,
        priceQuantity: formData.formValues.priceQuantity ?? null,
        priceReferenceQuantity: formData.formValues.priceReferenceQuantity ?? null,
        taxClass: formData.formValues.taxClass ?? TaxClass.Standard, // Shouldn't be null since we validate it in the form. Just a fallback
        currencyPrices,
    };
    return request;
});
